import { createContext, useCallback, useContext, useState } from "react";
import { WHATS_NEW_ID } from "./sections";

const SEEN_KEY = "help.whatsNewSeen";

/**
 * Lets any screen open the help — on a particular topic, if it names one
 * ("openHelp('products')"). Provided by the layout, which owns the panel.
 */
export const HelpOpenerContext = createContext(() => {});
export const useHelpOpener = () => useContext(HelpOpenerContext);

const readSeen = () => {
  try {
    return localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
};

/**
 * Whether the help is open, and whether this device has read the latest
 * "What's new" yet. The second is remembered per device, like the language:
 * browser storage can be cleared, in which case the dot simply comes back.
 */
export default function useHelp() {
  const [open, setOpen] = useState(false);
  // The panel and its text are only fetched the first time someone asks for help
  const [loaded, setLoaded] = useState(false);
  const [seen, setSeen] = useState(readSeen);
  const [topic, setTopic] = useState(null);

  const markWhatsNewSeen = useCallback(() => {
    try {
      localStorage.setItem(SEEN_KEY, WHATS_NEW_ID);
    } catch {
      // A private window just shows the dot again next time
    }
    setSeen(WHATS_NEW_ID);
  }, []);

  return {
    open,
    loaded,
    // A button passes its click event; only a topic name counts
    topic,
    openHelp: useCallback((key) => {
      setTopic(typeof key === "string" ? key : null);
      setLoaded(true);
      setOpen(true);
    }, []),
    closeHelp: useCallback(() => setOpen(false), []),
    hasNews: seen !== WHATS_NEW_ID,
    markWhatsNewSeen,
  };
}
